//AgentPanel.jsx


import React, { useState } from "react";
import { runAgent } from "../api/reportApi";
import "../styles/AgentPanel.css";

const exampleGroups = [
    {
        title: "Architecture risks",
        questions: [
            "Which module creates the highest architectural risk and why?",
            "What is the strongest indication of excessive coupling?",
            "Which module has the strongest concentration of architectural problems?"
        ]
    },
    {
        title: "Code hotspots",
        questions: [
            "Which class is the biggest architectural hotspot and why?",
            "Which architectural problem should be addressed first?"
        ]
    },
    {
        title: "Refactoring insights",
        questions: [
            "Which refactoring would likely reduce the most architectural risk, and what evidence supports it?",
            "If I could fix only one hotspot this week, which should I choose and why?"
        ]
    }
];

export default function AgentPanel({ jobId, user }) {
    const [task, setTask] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const isFree = !user?.plan || user.plan === "FREE";

    function handleExampleClick(question) {
        setTask(question);
        setError("");
    }

    async function handleSubmit(e) {
        e.preventDefault();

        if (isFree) {
            return;
        }

        if (!task.trim()) {
            setError("Enter a question for Agent");
            return;
        }

        setLoading(true);
        setError("");
        setResult(null);

        try {
            const response = await runAgent(jobId, task);
            setResult(response);
        } catch (err) {
            setError(err.message || "Agent request failed");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="agent-panel">
            <div className="agent-header">
                <div className="agent-title-row">
                    <div className="agent-icon" aria-hidden="true">
                        ✳
                    </div>

                    <div>
                        <h2>Custor Agent</h2>
                        <p>
                            Explore your architecture. Understand the risks.
                            Decide what to improve first.
                        </p>
                    </div>
                </div>
            </div>

            {isFree ? (
                <div className="agent-upgrade">
                    <h3>Agent is available on PRO</h3>
                    <p>
                        Upgrade your plan to use Custor Agent.
                    </p>
                    <button
                        type="button"
                        onClick={() => {
                            window.location.href = "/app/plan";
                        }}
                    >
                        Upgrade to PRO
                    </button>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="agent-form">
                    <div className="agent-examples">
                        <div className="agent-examples-heading">
                            <span>EXPLORE YOUR CODEBASE</span>
                            <span>TRY A QUESTION</span>
                        </div>

                        {exampleGroups.map((group) => (
                            <div
                                className="agent-example-group"
                                key={group.title}
                            >
                                <h3>{group.title}</h3>

                                <div className="agent-question-list">
                                    {group.questions.map((question) => (
                                        <button
                                            type="button"
                                            className="agent-question"
                                            key={question}
                                            onClick={() =>
                                                handleExampleClick(question)
                                            }
                                            disabled={loading}
                                        >
                                            <span>{question}</span>
                                            <span
                                                className="agent-question-arrow"
                                                aria-hidden="true"
                                            >
                                                ↗
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="agent-composer">
                        <label htmlFor="agent-task">
                            ASK ABOUT THIS ANALYSIS
                        </label>

                        <textarea
                            id="agent-task"
                            value={task}
                            onChange={(e) => setTask(e.target.value)}
                            placeholder="Ask about risks, dependencies, hotspots or refactoring priorities..."
                            disabled={loading}
                            rows={3}
                        />

                        <div className="agent-composer-footer">
                            <span>
                                Specific questions lead to more useful insights.
                            </span>

                            <button
                                type="submit"
                                disabled={loading || !task.trim()}
                            >
                                {loading ? "Analyzing..." : "Ask Agent"}
                                <span aria-hidden="true"> ↗</span>
                            </button>
                        </div>
                    </div>
                </form>
            )}

            {error && (
                <div className="agent-error" role="alert">
                    {error}
                </div>
            )}

            {result && (
                <div className="agent-result">
                    <div
                        className={`agent-result-status ${
                            result.success ? "is-success" : "is-failed"
                        }`}
                    >
                        {result.success ? "Completed" : "Failed"}
                    </div>

                    {result.summary && (
                        <div className="agent-summary">
                            {result.summary}
                        </div>
                    )}

                    {result.decision?.hypothesis?.statement && (
                        <div className="agent-answer">
                            <h3>Answer</h3>
                            <p>
                                {result.decision.hypothesis.statement}
                            </p>
                        </div>
                    )}

                    {result.decision?.hypothesis?.rationale && (
                        <div className="agent-rationale">
                            <h3>Reasoning</h3>
                            <p>
                                {result.decision.hypothesis.rationale}
                            </p>
                        </div>
                    )}

                    {result.decision?.validationSummary && (
                        <div className="agent-validation">
                            <h3>Validation</h3>
                            <p>
                                {result.decision.validationSummary}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
